import React, { useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuthStore } from '@/store/authStore';
import { useNavigationStore } from '@/store/panelStore';
import { 
  Users, 
  BookOpen, 
  User, 
  Settings, 
  ChevronLeft, 
  ChevronRight,
  Home,
  UserCog
} from 'lucide-react';

export const NavigationPanel: React.FC = () => {
  const { isCurator } = useAuthStore();
  const { isMobile, isCollapsed, toggleCollapse, setActivePage } = useNavigationStore();
  const location = useLocation();
  const sidebarRef = useRef<HTMLDivElement>(null);

  // Set CSS custom property for sidebar width
  useEffect(() => {
    const updateSidebarWidth = () => {
      if (sidebarRef.current && !isMobile) {
        const width = sidebarRef.current.offsetWidth;
        document.documentElement.style.setProperty('--sidebar-width', `${width}px`);
      } else {
        document.documentElement.style.setProperty('--sidebar-width', '0px');
      }
    };

    // Update immediately for initial load and resize
    updateSidebarWidth();
    window.addEventListener('resize', updateSidebarWidth);

    // For collapse/expand, update continuously during animation
    let animationFrameId: number | null = null;
    let startTime: number | null = null;
    const animationDuration = 300; // Match the CSS transition duration

    const animateWidth = (currentTime: number) => {
      if (startTime === null) {
        startTime = currentTime;
      }

      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / animationDuration, 1);

      // Update width during animation
      updateSidebarWidth();

      // Continue animation if not complete
      if (progress < 1) {
        animationFrameId = requestAnimationFrame(animateWidth);
      } else {
        // Animation complete, ensure final update
        updateSidebarWidth();
        animationFrameId = null;
        startTime = null;
      }
    };

    // Start animation when collapse state changes
    if (sidebarRef.current) {
      animationFrameId = requestAnimationFrame(animateWidth);
    }

    return () => {
      window.removeEventListener('resize', updateSidebarWidth);
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
    };
  }, [isMobile, isCollapsed]); // Re-run when mobile state or collapse state changes

  const navigationItems = [
    {
      path: '/rooms',
      icon: Users,
      label: isCurator() ? 'Manage Rooms' : 'Browse Rooms',
      mobileLabel: isCurator() ? 'Rooms' : 'Rooms',
      color: 'text-blue-600 dark:text-blue-400',
      bgColor: 'bg-blue-100 dark:bg-blue-900/20'
    },
    ...(isCurator() ? [{
      path: '/subjects',
      icon: BookOpen,
      label: 'Manage Subjects',
      mobileLabel: 'Subjects',
      color: 'text-green-600 dark:text-green-400',
      bgColor: 'bg-green-100 dark:bg-green-900/20'
    }, {
      path: '/manage-users',
      icon: UserCog,
      label: 'Manage Users',
      mobileLabel: 'Users',
      color: 'text-rose-600 dark:text-rose-400',
      bgColor: 'bg-rose-100 dark:bg-rose-900/20'
    }] : []),
    {
      path: '/profile',
      icon: User,
      label: 'Profile',
      color: 'text-purple-600 dark:text-purple-400',
      bgColor: 'bg-purple-100 dark:bg-purple-900/20'
    },
    {
      path: '/settings',
      icon: Settings,
      label: 'Settings',
      color: 'text-orange-600 dark:text-orange-400',
      bgColor: 'bg-orange-100 dark:bg-orange-900/20'
    }
  ];

  const handleNavClick = (path: string) => {
    setActivePage(path);
  };

  const isActive = (path: string) => {
    return location.pathname === path || 
           (path === '/rooms' && location.pathname.startsWith('/chat/'));
  };

  return (
    <>
      {/* Desktop Sidebar */}
      <div 
        ref={sidebarRef}
        className={`
        absolute top-0 left-0 h-full bg-white/95 dark:bg-gray-800/95 backdrop-blur-md shadow-xl border-r border-gray-200/50 dark:border-gray-700/50 z-40
        ${isMobile ? 'hidden' : ''}
        ${isCollapsed ? 'w-16' : 'w-64'} 
        transition-all duration-300 ease-in-out
      `}>
      {/* Header */}
      <div className="flex items-center justify-between border-b border-gray-200/50 dark:border-gray-700/50 p-4 h-16">
        {!isCollapsed ? (
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 dark:bg-blue-900/20 rounded-lg mr-3">
              <Home className="h-5 w-5 text-blue-600 dark:text-blue-400" />
            </div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              Navigation
            </h2>
          </div>
        ) : (
          <div className="w-8 h-8"></div> // Placeholder to maintain consistent spacing
        )}
        
        <button
          onClick={toggleCollapse}
          className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {isCollapsed ? (
            <ChevronRight className="h-4 w-4" />
          ) : (
            <ChevronLeft className="h-4 w-4" />
          )}
        </button>
      </div>

      {/* Navigation Items */}
      <nav>
        {navigationItems.map((item) => {
          const Icon = item.icon;
          const active = isActive(item.path);
          
          return (
            <Link
              key={item.path}
              to={item.path}
              onClick={() => handleNavClick(item.path)}
              className={`
                flex items-center transition-all duration-200 group relative h-16
                ${active ? `${item.bgColor} ${item.color} shadow-sm` 
                  : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-gray-200'
                }
              `}
              title={isCollapsed ? item.label : ''}
            >
              {/* Icon container - same absolute position in both states */}
              <div className="pl-5">
                <Icon className={`
                  h-6 w-6 transition-colors
                  ${active ? item.color : 'text-gray-500 dark:text-gray-400'}
                `} />
              </div>
              
              {/* Text container - slides in/out */}
              <div className={`
                overflow-hidden transition-all duration-300 py-3
                ${isCollapsed ? 'w-0 opacity-0' : 'w-auto opacity-100'}
                ${!isCollapsed ? 'ml-12' : ''}
              `}>
                <span className={`
                  text-sm font-medium whitespace-nowrap
                  ${active ? item.color : 'text-gray-700 dark:text-gray-300'}
                `}>
                  {item.label}
                </span>
              </div>

              {/* Active indicator */}
              <div className={`
                absolute right-2 w-1 h-6 bg-current rounded-full transition-all duration-300
                ${active && !isCollapsed ? 'opacity-100' : 'opacity-0'}
              `} />
            </Link>
          );
        })}
      </nav>

      </div>

      {/* Mobile Navigation (Bottom Bar) */}
      {isMobile && !location.pathname.startsWith('/chat/') && (
        <div className="fixed bottom-0 left-0 right-0 bg-white/95 dark:bg-gray-800/95 backdrop-blur-md shadow-xl border-t border-gray-200/50 dark:border-gray-700/50 z-50">
          <div className="flex items-center justify-around">
            {navigationItems.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.path);
              
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => handleNavClick(item.path)}
                  className={`
                    flex flex-col items-center p-1 transition-all duration-200 w-full
                    ${active ? `${item.bgColor} ${item.color} shadow-sm`
                      : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-gray-200'
                    }
                  `}
                >
                  <div className={`
                    p-2 rounded-lg transition-transform
                    ${active ? item.bgColor : 'bg-transparent'}
                  `}>
                    <Icon className="h-7 w-7" />
                  </div>
                  <span className="text-xs font-medium mt-1">
                    {item.mobileLabel || item.label}
                  </span>
                </Link>
              );
            })}
          </div>
        </div>
      )}
    </>
  );
};
